import { Injectable } from '@angular/core';
import { ReportingApiService } from '@epicuro-next/data/reporting-api';
import { selectConnectToken } from '@epicuro-next/state/connect-token-state';
import { isEqual, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  switchMap,
} from 'rxjs/operators';

import {
  AnalyticsSignalREvent,
  IListPlacedReportSignalrEvent,
} from '../../models/signalr-events';
import {
  loadHotListAnalytics,
  loadListsReports,
  reportDataReceived,
  setHottestLists,
} from '../analytics-entities.actions';
import {
  selectHotListDateRange,
  selectHotListFeatureConfiguration,
} from '../selectors';

@Injectable()
export class AnalyticsBestListsEffects {
  private hotListFeatureConfig$ = this.store.pipe(
    select(selectHotListFeatureConfiguration),
    distinctUntilChanged<ReturnType<typeof selectHotListFeatureConfiguration>>(
      isEqual,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private hotListDateRange$ = this.store.pipe(
    select(selectHotListDateRange),
    distinctUntilChanged<ReturnType<typeof selectHotListDateRange>>(isEqual),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private connectToken$ = this.store.pipe(
    select(selectConnectToken),
    distinctUntilChanged(),
  );

  private hotListArgs$ = combineLatest([
    this.hotListFeatureConfig$.pipe(filter(isNonNullish)),
    this.hotListDateRange$.pipe(filter(isNonNullish)),
    this.connectToken$.pipe(filter(isNonNullish)),
  ]).pipe(
    map(([config, dateRange]) => {
      if (!dateRange) return undefined;
      return {
        config,
        dateRange,
      };
    }),
  );

  public dispatchLoadingHotList$ = createEffect(() =>
    this.hotListArgs$.pipe(
      distinctUntilChanged(isEqual),
      mapTo(loadHotListAnalytics()),
    ),
  );

  public updateHotListAnalyticsRealTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportDataReceived),
      concatLatestFrom(() => [this.hotListFeatureConfig$]),
      switchMap(([action, config]) => {
        if (!config || !action.event) return [];

        const event =
          action.event.eventName === AnalyticsSignalREvent.ListPlacedReportEvent
            ? (action.event as IListPlacedReportSignalrEvent)
            : undefined;

        return [
          loadHotListAnalytics(),
          event
            ? loadListsReports({ listIds: [event.payload.productListId] })
            : undefined,
        ].filter(isNonNullish);
      }),
    ),
  );

  public loadBestList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHotListAnalytics),
      concatLatestFrom(() => [this.hotListArgs$.pipe(filter(isNonNullish))]),
      switchMap(([, { config, dateRange }]) =>
        this.reportingService
          .getTopListRevenues(config.amountOfHotLists, dateRange)
          .pipe(
            map((topLists) => setHottestLists({ topLists })),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  constructor(
    private reportingService: ReportingApiService,
    private store: Store,
    private actions$: Actions,
  ) {}
}
